/**
 * Block Registry for NurtureCRM Website & Funnel Builder
 * Defines all available block types, their defaults, categories, and variants.
 */

export const BLOCK_CATEGORIES = [
  { id: 'layout', label: 'Layout', icon: 'LayoutGrid' },
  { id: 'content', label: 'Content', icon: 'Type' },
  { id: 'marketing', label: 'Marketing', icon: 'Megaphone' },
  { id: 'crm', label: 'CRM', icon: 'Database' },
  { id: 'advanced', label: 'Advanced', icon: 'Code' },
];

export const BLOCK_REGISTRY = {
  hero: {
    label: 'Hero Section',
    icon: 'Monitor',
    category: 'layout',
    variants: ['centered', 'split_image', 'video_bg', 'gradient'],
    defaultProps: {
      headline: 'Your Headline Here',
      subheadline: 'Supporting text that explains your value proposition',
      cta_text: 'Get Started',
      cta_url: '#',
      background_color: '#1c1917',
      text_color: '#ffffff',
      alignment: 'center',
      min_height: '75vh',
    },
  },
  text: {
    label: 'Text Block',
    icon: 'Type',
    category: 'content',
    variants: ['default', 'two_column'],
    defaultProps: {
      content: '<p>Start typing your content here...</p>',
      max_width: 'prose',
    },
  },
  image: {
    label: 'Image',
    icon: 'ImageIcon',
    category: 'content',
    variants: ['default', 'full_width', 'with_caption'],
    defaultProps: {
      image_url: '',
      alt_text: '',
      caption: '',
      rounded: true,
    },
  },
  features: {
    label: 'Features',
    icon: 'LayoutGrid',
    category: 'marketing',
    variants: ['grid_3', 'grid_2', 'alternating', 'icon_list'],
    defaultProps: {
      headline: 'Features',
      subheadline: '',
      items: [
        { icon: 'Zap', title: 'Feature 1', description: 'Description' },
        { icon: 'Shield', title: 'Feature 2', description: 'Description' },
        { icon: 'Sparkles', title: 'Feature 3', description: 'Description' },
      ],
    },
  },
  cta: {
    label: 'Call to Action',
    icon: 'MousePointerClick',
    category: 'marketing',
    variants: ['banner', 'split', 'simple'],
    defaultProps: {
      headline: 'Ready to get started?',
      subheadline: 'Start your free trial today',
      cta_text: 'Get Started',
      cta_url: '#',
      background_color: '#1c1917',
      text_color: '#ffffff',
    },
  },
  footer: {
    label: 'Footer',
    icon: 'PanelBottom',
    category: 'layout',
    variants: ['simple', 'multi_column', 'minimal'],
    defaultProps: {
      company_name: '',
      links: [],
      copyright_text: '\u00a9 2026 All rights reserved.',
      show_branding: true,
    },
  },
  form: {
    label: 'Form',
    icon: 'FileText',
    category: 'crm',
    variants: ['contact', 'newsletter', 'booking_request', 'custom'],
    defaultProps: {
      headline: 'Get in Touch',
      subheadline: 'Fill out the form below and we\'ll get back to you shortly.',
      fields: [
        { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your full name' },
        { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
        { name: 'phone', label: 'Phone', type: 'tel', required: false, placeholder: '+1 (555) 000-0000' },
        { name: 'message', label: 'Message', type: 'textarea', required: false, placeholder: 'How can we help?' },
      ],
      submit_text: 'Submit',
      success_message: 'Thanks! We\'ll be in touch soon.',
      background_color: '#1c1917',
      text_color: '#ffffff',
      form_style: 'card',
    },
  },
  testimonials: {
    label: 'Testimonials',
    icon: 'MessageSquare',
    category: 'marketing',
    variants: ['cards', 'carousel', 'quotes', 'minimal'],
    defaultProps: {
      headline: 'What Our Customers Say',
      subheadline: 'Trusted by teams worldwide',
      items: [
        { name: 'Sarah Johnson', role: 'VP of Sales', company: 'TechCorp', quote: 'This platform completely transformed how our team manages relationships. The AI insights are a game-changer.', avatar_url: '', rating: 5 },
        { name: 'Michael Chen', role: 'CEO', company: 'GrowthLabs', quote: 'We closed 40% more deals in the first quarter after switching. The DISC profiling alone is worth it.', avatar_url: '', rating: 5 },
        { name: 'Emily Rodriguez', role: 'Sales Director', company: 'Innovate Inc', quote: 'The best CRM we\'ve ever used. Clean interface, powerful AI, and our team actually enjoys using it.', avatar_url: '', rating: 4 },
      ],
      show_ratings: true,
      background_color: '#FAFAF5',
    },
  },
  pricing: {
    label: 'Pricing',
    icon: 'DollarSign',
    category: 'marketing',
    variants: ['cards', 'comparison', 'simple'],
    defaultProps: {
      headline: 'Simple, Transparent Pricing',
      subheadline: 'Choose the plan that fits your team',
      plans: [
        {
          name: 'Starter',
          price: '$29',
          period: 'month',
          description: 'For small teams getting started',
          features: ['Up to 5 users', '1,000 contacts', 'Email integration', 'Basic reporting'],
          cta_text: 'Start Free Trial',
          cta_url: '#',
          is_popular: false,
          badge_text: '',
        },
        {
          name: 'Professional',
          price: '$79',
          period: 'month',
          description: 'For growing sales teams',
          features: ['Up to 25 users', '10,000 contacts', 'AI email writer', 'DISC profiling', 'Campaign builder', 'Priority support'],
          cta_text: 'Start Free Trial',
          cta_url: '#',
          is_popular: true,
          badge_text: 'Most Popular',
        },
        {
          name: 'Enterprise',
          price: '$199',
          period: 'month',
          description: 'For large organizations',
          features: ['Unlimited users', 'Unlimited contacts', 'Custom integrations', 'Dedicated support', 'SSO & security', 'API access'],
          cta_text: 'Contact Sales',
          cta_url: '#',
          is_popular: false,
          badge_text: '',
        },
      ],
      background_color: '#FAFAF5',
    },
  },
  booking: {
    label: 'Booking',
    icon: 'Calendar',
    category: 'crm',
    variants: ['inline', 'button', 'card'],
    defaultProps: {
      headline: 'Book a Meeting',
      subheadline: 'Choose a time that works for you',
      booking_link_id: '',
      booking_url: '#',
      button_text: 'Book Now',
      description: 'Schedule a 30-minute call with our team to discuss how we can help you grow.',
      duration: '30 min',
      background_color: '#1c1917',
      text_color: '#ffffff',
    },
  },
  faq: {
    label: 'FAQ',
    icon: 'HelpCircle',
    category: 'content',
    variants: ['accordion', 'two_column', 'simple'],
    defaultProps: {
      headline: 'Frequently Asked Questions',
      subheadline: 'Everything you need to know',
      items: [
        { question: 'How does the free trial work?', answer: 'You get full access to all features for 14 days. No credit card required. Cancel anytime.' },
        { question: 'Can I change my plan later?', answer: 'Yes, you can upgrade or downgrade your plan at any time from your account settings.' },
        { question: 'Is my data secure?', answer: 'Absolutely. We use industry-standard encryption and security practices to keep your data safe.' },
      ],
      background_color: '#FAFAF5',
    },
  },
  stats: {
    label: 'Statistics',
    icon: 'BarChart3',
    category: 'marketing',
    variants: ['horizontal', 'cards', 'minimal'],
    defaultProps: {
      headline: '',
      items: [
        { value: '10K+', label: 'Happy Customers', prefix: '', suffix: '' },
        { value: '99%', label: 'Uptime', prefix: '', suffix: '' },
        { value: '50M', label: 'Emails Sent', prefix: '', suffix: '' },
        { value: '4.9', label: 'Average Rating', prefix: '', suffix: '/5' },
      ],
      background_color: '#1c1917',
      text_color: '#ffffff',
    },
  },
  logo_cloud: {
    label: 'Logo Cloud',
    icon: 'Grid3X3',
    category: 'marketing',
    variants: ['grid', 'scroll', 'simple'],
    defaultProps: {
      headline: 'Trusted by leading companies',
      logos: [
        { name: 'Company 1', image_url: '', url: '' },
        { name: 'Company 2', image_url: '', url: '' },
        { name: 'Company 3', image_url: '', url: '' },
        { name: 'Company 4', image_url: '', url: '' },
        { name: 'Company 5', image_url: '', url: '' },
      ],
      grayscale: true,
      background_color: '#FAFAF5',
    },
  },
  video: {
    label: 'Video',
    icon: 'PlayCircle',
    category: 'content',
    variants: ['youtube', 'vimeo', 'custom'],
    defaultProps: {
      headline: '',
      video_url: '',
      thumbnail_url: '',
      autoplay: false,
      aspect_ratio: '16:9',
      background_color: '#ffffff',
    },
  },
  divider: {
    label: 'Divider',
    icon: 'Minus',
    category: 'layout',
    variants: ['line', 'wave', 'spacer', 'dots'],
    defaultProps: {
      height: '40px',
      color: '#d6d3d1',
      style_variant: 'solid',
    },
  },
  gallery: {
    label: 'Gallery',
    icon: 'Images',
    category: 'content',
    variants: ['grid', 'carousel', 'lightbox'],
    defaultProps: {
      headline: 'Gallery',
      images: [
        { image_url: '', alt_text: 'Image 1', caption: '' },
        { image_url: '', alt_text: 'Image 2', caption: '' },
        { image_url: '', alt_text: 'Image 3', caption: '' },
      ],
      columns: 3,
      gap: 'md',
      rounded: true,
    },
  },
  html: {
    label: 'Custom HTML',
    icon: 'Code',
    category: 'advanced',
    variants: ['default', 'full_width', 'contained'],
    defaultProps: {
      html_content: '<div style="padding: 2rem; text-align: center; color: #78716c;">Paste your HTML here</div>',
      custom_css: '',
      max_width: 'full',
    },
  },
};

/**
 * Generate a new block object with a unique ID, the given type,
 * the first variant as default, and a deep copy of default props.
 */
export function getBlockDefaults(blockType) {
  const definition = BLOCK_REGISTRY[blockType];
  if (!definition) {
    throw new Error(`Unknown block type: ${blockType}`);
  }

  return {
    block_id: 'blk_' + Math.random().toString(36).slice(2, 10),
    block_type: blockType,
    variant: definition.variants[0],
    props: JSON.parse(JSON.stringify(definition.defaultProps)),
  };
}
